import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef } from "react";
import { IBulkOpsState } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";

export interface ISwapACBatchTracker {
  batchId1: string;
  batchId2: string;
  numberOfActions: number;
  setBatchId1: (vaule: string) => void;
  setBatchId2: (vaule: string) => void;
}

/**
 * A component to track the swap batches progress
 * @returns null
 */

export const SwapACBatchTracker = (props: ISwapACBatchTracker): JSX.Element => {
  const dispatch = useDispatch();

  const { batchId1, batchId2, numberOfActions, setBatchId1, setBatchId2 } = props;
  const batchJob1: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId1]);
  const eventsUpdated1 = batchJob1 ? batchJob1?.successfulEventIds?.length : 0;
  const eventsFailed1 = batchJob1 ? batchJob1?.failedEvents?.length : 0;

  const batchJob2: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId2]);
  const eventsUpdated2 = batchJob2 ? batchJob2?.successfulEventIds?.length : 0;
  const eventsFailed2 = batchJob2 ? batchJob2?.failedEvents?.length : 0;

  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);

  // The completion handler needs the current selection, but it must not be a dependency
  // of the effect that *sets* the selection: `.filter()` returns a fresh array every run,
  // so dispatching it would re-arm this effect on the next render.
  const selectedEventsRef = useRef(selectedEvents);
  selectedEventsRef.current = selectedEvents;

  // Reports a given pair of batches exactly once.
  const handledBatchRef = useRef<string | null>(null);
  const batchKey = `${batchId1 ?? ""}|${batchId2 ?? ""}`;

  const actionsAttempted: number = eventsUpdated1 + eventsFailed1 + eventsUpdated2 + eventsFailed2;
  const finishedAction: boolean = useMemo(() => {
    if (actionsAttempted === numberOfActions) {
      return true;
    }
    return false;
  }, [actionsAttempted, numberOfActions]);

  useEffect(() => {
    // No swap in flight, so there is nothing to report. Both counters read 0 once a
    // successful swap clears the batch ids and empties the selection, which leaves
    // `finishedAction` permanently true - without this guard the effect re-dispatches
    // on every render.
    if (!batchId1 && !batchId2) return;
    if (!finishedAction) return;
    if (handledBatchRef.current === batchKey) return;
    handledBatchRef.current = batchKey;

    if (eventsUpdated1 + eventsUpdated2 === numberOfActions) {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "Aircraft Events Successfully Swapped",
        })
      );
    } else {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: `Something Went Wrong! ${eventsFailed1 + eventsFailed2} failed`,
        })
      );
    }

    setBatchId1(null);
    setBatchId2(null);

    const succeededIds = new Set<string>([
      ...(batchJob1?.successfulEventIds?.map((m) => m.objectId) ?? []),
      ...(batchJob2?.successfulEventIds?.map((m) => m.objectId) ?? []),
    ]);
    const failedEvents = selectedEventsRef.current.filter((flight) => !succeededIds.has(String(flight._id)));
    dispatch(setSelectedEvents(failedEvents));
  }, [
    batchId1,
    batchId2,
    batchJob1,
    batchJob2,
    batchKey,
    dispatch,
    eventsFailed1,
    eventsFailed2,
    eventsUpdated1,
    eventsUpdated2,
    finishedAction,
    numberOfActions,
    setBatchId1,
    setBatchId2,
  ]);

  return null;
};
